import { useState } from 'react';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5oc05sl56fkiit4o190', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header id="header" className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
        <div className="max-w-[1400px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/9fdeb711-7386-492b-b93c-7da8e86a371e.png" 
              alt="GEZA LTDA Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-base font-light tracking-[3px] text-white/85">GEZA LTDA</span>
          </div>
          <nav className="flex items-center gap-10">
            <button onClick={() => scrollToSection('about')} className="text-sm font-light text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-sm font-light text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-sm font-light text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-light text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-b from-[#1a2847] to-[#4a5568] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-[15%] left-0 w-[40%] h-[1px] bg-white/20"></div>
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] rounded-full bg-gradient-radial from-[#0066FF]/30 to-transparent blur-3xl"></div>
        
        <div className="relative max-w-[1400px] mx-auto px-20 w-full">
          <div className="flex items-center justify-between">
            <div className="max-w-[600px]">
              <h1 className="text-[92px] leading-[1.1] mb-0">
                <span className="block font-extralight text-white">iOS App</span>
                <span className="block font-medium text-white">Development</span>
                <span className="block font-bold text-white">Experts</span>
              </h1>
            </div>
            
            <div className="max-w-[420px] text-right">
              <p className="text-[22px] font-light text-white/70 leading-relaxed">
                Enhancing mobile security and user experience through innovative iOS applications
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-[#F8F8F8] py-48">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="relative">
            <div className="absolute -top-8 -left-4 text-[180px] font-serif text-[#0066FF]/15 leading-none">"</div>
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-[2.5px] text-[#6B6B6B] mb-8">ABOUT US</p>
              <h2 className="text-[72px] font-bold font-serif text-[#1A1A1A] leading-[1.1] mb-14">
                A Team of<br />Experts
              </h2>
              <div className="space-y-10 max-w-[900px]">
                <p className="text-[19px] leading-[1.9] text-[#3A3A3A]">
                  GEZA LTDA is a dedicated team of professionals specializing in the development of cutting-edge applications for the iOS platform. We combine technical expertise with creative innovation to deliver solutions that truly make a difference.
                </p>
                <p className="text-[19px] leading-[1.9] text-[#3A3A3A]">
                  Our flagship products, including our VPN and Cleaner applications, are meticulously designed to enhance user experience while prioritizing the security and performance of mobile devices. We understand the evolving needs of iOS users and continuously adapt our solutions to meet those demands.
                </p>
                <p className="text-[19px] leading-[1.9] text-[#3A3A3A]">
                  With a focus on quality, reliability, and user satisfaction, we strive to create applications that not only solve problems but also provide seamless and intuitive experiences for our users worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-white py-44">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="text-center mb-32">
            <h2 className="text-[68px] font-extrabold font-serif text-[#1A1A1A] mb-10">Our Products</h2>
            <p className="text-lg text-[#6B6B6B]">Designed for modern iOS users</p>
          </div>
          
          <div className="grid grid-cols-2 gap-20">
            {/* VPN Product Card */}
            <div className="bg-gradient-to-b from-[#F0F7FF] to-white rounded-[32px] border border-[#E8E8E8] p-20 shadow-[0_8px_40px_rgba(0,0,0,0.04)]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#0066FF] mb-6">SECURITY</p>
              <h3 className="text-[56px] font-bold font-serif text-[#1A1A1A] mb-8">VPN</h3>
              <p className="text-[17px] leading-[1.8] text-[#4A4A4A] mb-12">
                Our VPN application provides robust security and privacy protection for iOS users. With military-grade encryption and a global network of servers, you can browse the internet safely and access content from anywhere in the world without compromising your personal data.
              </p>
              
              <ul className="space-y-8 mb-16">
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Military-grade encryption for maximum security</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Global server network for unrestricted access</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">No-logs policy ensuring complete privacy</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#0066FF] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Lightning-fast connection speeds</span>
                </li>
              </ul>
              
              <div className="flex gap-4 opacity-40">
                <div className="w-24 h-24 rounded-2xl bg-[#0066FF]/30"></div>
                <div className="w-32 h-24 rounded-2xl bg-[#0066FF]/20"></div>
                <div className="w-20 h-24 rounded-2xl bg-[#0066FF]/25"></div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-gradient-to-b from-[#F0FFF7] to-white rounded-[32px] border border-[#E8E8E8] p-20 shadow-[0_8px_40px_rgba(0,0,0,0.04)]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#00CC66] mb-6">OPTIMIZATION</p>
              <h3 className="text-[56px] font-bold font-serif text-[#1A1A1A] mb-8">Cleaner</h3>
              <p className="text-[17px] leading-[1.8] text-[#4A4A4A] mb-12">
                Keep your iOS device running at peak performance with our Cleaner application. Intelligently identify and remove unnecessary files, optimize storage, and boost your device's speed with just a few taps. Experience your iPhone or iPad like new again.
              </p>
              
              <ul className="space-y-8 mb-16">
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#00CC66] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Smart storage analysis and optimization</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#00CC66] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Safe removal of duplicate and junk files</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#00CC66] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Performance boost for faster operation</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#00CC66] mt-2 flex-shrink-0"></div>
                  <span className="text-base font-medium text-[#1A1A1A]">Battery life optimization features</span>
                </li>
              </ul>
              
              <div className="flex gap-4 opacity-40">
                <div className="w-28 h-28 rounded-full bg-[#00CC66]/30 border-4 border-[#00CC66]/20"></div>
                <div className="w-20 h-20 rounded-full bg-[#00CC66]/25 border-4 border-[#00CC66]/15 mt-4"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-white py-40">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="text-center mb-28">
            <h2 className="text-[64px] font-bold font-serif text-[#1A1A1A] mb-9">Enhancing Your iOS Experience</h2>
            <p className="text-lg text-[#6B6B6B]">Security, performance, and simplicity combined</p>
          </div>
          
          <div className="grid grid-cols-4 gap-10">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0066FF]/10 to-[#0066FF]/5 flex items-center justify-center mx-auto mb-8">
                <i className="ri-shield-check-line text-2xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-[22px] font-semibold text-[#1A1A1A] mb-5">Data Encryption</h3>
              <p className="text-[15px] leading-[1.7] text-[#6B6B6B] max-w-[280px] mx-auto">
                Advanced encryption protocols protect your sensitive information from unauthorized access and cyber threats.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00CC66]/10 to-[#00CC66]/5 flex items-center justify-center mx-auto mb-8">
                <i className="ri-dashboard-line text-2xl text-[#00CC66]"></i>
              </div>
              <h3 className="text-[22px] font-semibold text-[#1A1A1A] mb-5">Storage Optimization</h3>
              <p className="text-[15px] leading-[1.7] text-[#6B6B6B] max-w-[280px] mx-auto">
                Intelligent algorithms identify and remove unnecessary files, freeing up valuable storage space on your device.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0066FF]/10 to-[#0066FF]/5 flex items-center justify-center mx-auto mb-8">
                <i className="ri-lock-line text-2xl text-[#0066FF]"></i>
              </div>
              <h3 className="text-[22px] font-semibold text-[#1A1A1A] mb-5">Privacy Protection</h3>
              <p className="text-[15px] leading-[1.7] text-[#6B6B6B] max-w-[280px] mx-auto">
                Browse anonymously and keep your online activities private with our comprehensive privacy features.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00CC66]/10 to-[#00CC66]/5 flex items-center justify-center mx-auto mb-8">
                <i className="ri-speed-line text-2xl text-[#00CC66]"></i>
              </div>
              <h3 className="text-[22px] font-semibold text-[#1A1A1A] mb-5">Performance Boost</h3>
              <p className="text-[15px] leading-[1.7] text-[#6B6B6B] max-w-[280px] mx-auto">
                Optimize system resources and enhance device performance for a smoother, faster iOS experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1E1E1E] py-24">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="grid grid-cols-2 gap-20">
            {/* Left Column - Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9fdeb711-7386-492b-b93c-7da8e86a371e.png" 
                  alt="GEZA LTDA Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h3 className="text-2xl font-serif text-white">GEZA LTDA</h3>
              </div>
              <p className="text-[15px] text-[#8A8A8A] mb-14">iOS Development Experts</p>
              
              <div className="space-y-10">
                <div>
                  <p className="text-[11px] uppercase tracking-[2px] text-[#6B6B6B] mb-6">CONTACT</p>
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <i className="ri-mail-line text-lg text-[#8A8A8A]"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">Email</p>
                        <p className="text-base text-white font-medium">information@gezaltda.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-phone-line text-lg text-[#8A8A8A]"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">Number</p>
                        <p className="text-base text-white font-medium">+1 8398882099</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <p className="text-[11px] uppercase tracking-[2px] text-[#6B6B6B] mb-6">LOCATION</p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-lg text-[#8A8A8A] mt-1"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">Address</p>
                        <p className="text-[15px] text-white">Av. PERCIO PERFEITO S/N S N</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-building-line text-lg text-[#8A8A8A] mt-1"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">City</p>
                        <p className="text-[15px] text-white">ARAGUARI</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-mail-send-line text-lg text-[#8A8A8A] mt-1"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">Zip</p>
                        <p className="text-[15px] text-white">38446-394</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-global-line text-lg text-[#8A8A8A] mt-1"></i>
                      <div>
                        <p className="text-sm text-[#8A8A8A]">Country</p>
                        <p className="text-[15px] text-white">Brazil</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div>
              <h3 className="text-3xl font-bold text-white mb-3">Get in Touch</h3>
              <p className="text-[15px] text-[#8A8A8A] mb-10">Have questions? We'd love to hear from you.</p>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm text-[#8A8A8A] mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm text-[#8A8A8A] mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm text-[#8A8A8A] mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                    placeholder="How can we help?"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm text-[#8A8A8A] mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#0066FF] transition-colors resize-none"
                    placeholder="Your message (max 500 characters)"
                  />
                  <p className="text-xs text-[#6A6A6A] mt-2">{formData.message.length}/500 characters</p>
                </div>
                
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium py-3 px-6 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
                
                {formStatus === 'success' && (
                  <div className="bg-[#00CC66]/10 border border-[#00CC66]/30 rounded-lg p-4 text-center">
                    <p className="text-[#00CC66] text-sm">Message sent successfully! We'll get back to you soon.</p>
                  </div>
                )}
                
                {formStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                    <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
          
          {/* Footer Bottom */}
          <div className="mt-20 pt-8 border-t border-[#3A3A3A] flex items-center justify-between">
            <p className="text-[13px] text-[#6A6A6A]">© 2025 GEZA LTDA. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#6A6A6A] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>

      {/* Scroll Effect for Header */}
      <script dangerouslySetInnerHTML={{
        __html: `
          window.addEventListener('scroll', function() {
            const header = document.getElementById('header');
            if (window.scrollY > 100) {
              header.style.backgroundColor = 'rgba(255, 255, 255, 0.98)';
              header.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.08)';
              header.querySelectorAll('span, button').forEach(el => {
                el.style.color = '#1A1A1A';
              });
            } else {
              header.style.backgroundColor = 'transparent';
              header.style.boxShadow = 'none';
              header.querySelectorAll('span').forEach(el => {
                el.style.color = 'rgba(255, 255, 255, 0.85)';
              });
              header.querySelectorAll('button').forEach(el => {
                el.style.color = 'rgba(255, 255, 255, 0.8)';
              });
            }
          });
        `
      }} />
    </div>
  );
}
